import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useNavigate } from 'react-router-dom';
import './App.css';
import Login from './components/Login';
import Home from './components/Home';
import Dashboard from './components/Dashboard';
import OpenOrdersDashboard from './components/OpenOrdersDashboard';
import Admin from './components/Admin';
import WarehouseProductivityDispatched from './components/WarehouseProductivityDispatched';
import WarehouseProductivityOpen from './components/WarehouseProductivityOpen';
import WarehouseProductivityHybrid from './components/WarehouseProductivityHybrid';
import UscoConverter from './components/usco/UscoConverter';
import { isAuthenticated, logout, canUseUsco } from './utils/auth';

// Protected Route component that checks auth on every render
function ProtectedRoute({ children }) {
  const [authChecked, setAuthChecked] = useState(false);
  const isAuth = isAuthenticated();

  useEffect(() => {
    setAuthChecked(true);
  }, []);

  if (!authChecked) {
    return null; // Prevent flash of wrong content
  }

  if (!isAuth) {
    return <Navigate to="/login" replace />;
  }

  return children;
}

function App() {
  const [authState, setAuthState] = useState(isAuthenticated());

  // Detect restored sessions (Ctrl+Shift+T, Chrome "Continue where you left off")
  // and clear auth so user must log in again after closing the tab/browser
  useEffect(() => {
    // Skip the restore check if we just landed here from an external auth redirect
    const justAuthenticated = sessionStorage.getItem('justAuthenticated');
    if (justAuthenticated) {
      sessionStorage.removeItem('justAuthenticated');
      localStorage.removeItem('pageUnloadTime');
    } else {
      const pageUnloadTime = localStorage.getItem('pageUnloadTime');
      if (pageUnloadTime) {
        const elapsed = Date.now() - parseInt(pageUnloadTime, 10);
        localStorage.removeItem('pageUnloadTime');
        if (elapsed > 5000) {
          // More than 5 seconds since unload — this is a restored session, not a refresh
          logout();
          setAuthState(false);
        }
      }
    }

    // Record page unload time on close/refresh so we can detect restored sessions
    const handleBeforeUnload = () => {
      localStorage.setItem('pageUnloadTime', Date.now().toString());
    };

    window.addEventListener('beforeunload', handleBeforeUnload);

    // Also check auth state periodically (handles token expiry)
    const interval = setInterval(() => {
      setAuthState(isAuthenticated());
    }, 1000);

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
      clearInterval(interval);
    };
  }, []);

  const handleLoginSuccess = () => {
    setAuthState(true);
  };

  return (
    <Router>
      <div className="App">
        <Routes>
          {/* Login route */}
          <Route path="/login" element={
            authState ? <Navigate to="/" replace /> : <Login onLoginSuccess={handleLoginSuccess} />
          } />

          {/* Protected Home route */}
          <Route path="/" element={
            <ProtectedRoute>
              <Home />
            </ProtectedRoute>
          } />

          {/* Protected Dispatched Orders route */}
          <Route path="/dispatched-orders" element={
            <ProtectedRoute>
              <Dashboard />
            </ProtectedRoute>
          } />

          {/* Protected Open Orders routes */}
          <Route path="/open-orders" element={
            <ProtectedRoute>
              <OpenOrdersDashboard />
            </ProtectedRoute>
          } />
          <Route path="/open-orders/:warehouse" element={
            <ProtectedRoute>
              <OpenOrdersDashboard />
            </ProtectedRoute>
          } />

          {/* Warehouse Productivity routes */}
          <Route path="/warehouse-dispatched" element={
            <ProtectedRoute><WarehouseProductivityDispatched /></ProtectedRoute>
          } />
          <Route path="/warehouse-open" element={
            <ProtectedRoute><WarehouseProductivityOpen /></ProtectedRoute>
          } />
          <Route path="/warehouse-hybrid" element={
            <ProtectedRoute><WarehouseProductivityHybrid /></ProtectedRoute>
          } />

          {/* USCO Converter - only the users the server allows (super admins + the USCO allowlist) */}
          <Route path="/usco-converter" element={
            <ProtectedRoute>
              {canUseUsco() ? <UscoConverter /> : <Navigate to="/" replace />}
            </ProtectedRoute>
          } />

          {/* Protected Admin route */}
          <Route path="/admin" element={
            <ProtectedRoute>
              <Admin />
            </ProtectedRoute>
          } />

          {/* Fallback for unknown routes */}
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </div>
    </Router>
  );
}

export default App;
